import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Send, Sparkles } from 'lucide-react'
import { api, ApiError, type ChatTurn } from '../api/client'
import { Button } from '../components/ui/Button'
import { LoadingDots } from '../components/ui/LoadingDots'
import { ErrorState } from '../components/ui/ErrorState'
import { SectionHeader } from '../components/ui/SectionHeader'
import { usePaywallStore } from '../store/paywall.store'

/**
 * "Спроси Ядвигу" — multi-turn chat (premium). UX:
 *
 *   - Top half: scrollable transcript of the current session (user right, Yadviga left)
 *   - Bottom: textarea + Send button, disabled when limit reached
 *   - Side rail (or above on mobile): list of recent sessions + "New chat" button
 *
 * Free users land here from the paywall trigger — we pop the paywall and let
 * them subscribe inline, then refetch.
 */
export function ChatRoute() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const transcriptEndRef = useRef<HTMLDivElement>(null)
  const showPaywall = usePaywallStore((s) => s.show)

  const sessionsQuery = useQuery({
    queryKey: ['chat', 'sessions'],
    queryFn: () => api.listChatSessions(),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 402) return false
      return count < 1
    },
  })

  const locked =
    sessionsQuery.isError &&
    sessionsQuery.error instanceof ApiError &&
    sessionsQuery.error.status === 402

  const activeQuery = useQuery({
    queryKey: ['chat', 'session', activeSessionId],
    queryFn: () => api.getChatSession(activeSessionId!),
    enabled: Boolean(activeSessionId),
  })

  // Auto-scroll to the bottom on new messages.
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [activeQuery.data?.messages.length])

  const startMutation = useMutation({
    mutationFn: () => api.startChatSession(),
    onSuccess: (session) => {
      setActiveSessionId(session.id)
      qc.setQueryData(['chat', 'session', session.id], session)
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        showPaywall({ reason: 'feature_locked', feature: 'chat_question' })
      }
    },
  })

  const postMutation = useMutation({
    mutationFn: (content: string) => api.postChatMessage(activeSessionId!, content),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'session', activeSessionId] })
      qc.invalidateQueries({ queryKey: ['chat', 'sessions'] })
    },
  })

  const handleSend = () => {
    const trimmed = draft.trim()
    if (!trimmed || !activeSessionId || postMutation.isPending) return
    setDraft('')
    postMutation.mutate(trimmed)
  }

  // ---------- locked state ----------
  if (locked) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <header className="flex flex-col gap-1">
          <h1 className="font-serif text-3xl text-sage-800">{t('chat.title')}</h1>
          <p className="text-sm text-sage-600">{t('chat.intro')}</p>
        </header>
        <div className="rounded-3xl bg-[#fbeadd] p-5 ring-1 ring-[#e6c4a4]/60">
          <div className="flex items-start gap-3">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-copper" strokeWidth={1.6} />
            <p className="text-sage-800 leading-relaxed">{t('chat.locked')}</p>
          </div>
          <Button
            size="md"
            className="mt-3"
            onClick={() => showPaywall({ reason: 'feature_locked', feature: 'chat_question' })}
          >
            {t('chat.open_premium')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 pb-6">
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="font-serif text-3xl text-sage-800">{t('chat.title')}</h1>
          <p className="text-sm text-sage-600">{t('chat.intro')}</p>
        </div>
        <Button
          size="md"
          variant="secondary"
          onClick={() => startMutation.mutate()}
          loading={startMutation.isPending}
        >
          {t('chat.new')}
        </Button>
      </header>

      {/* History rail — collapses above on mobile */}
      {sessionsQuery.data && sessionsQuery.data.length > 0 && (
        <section>
          <SectionHeader title={t('chat.history')} />
          <div className="flex flex-col gap-2">
            {sessionsQuery.data.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveSessionId(s.id)}
                className={`flex flex-col items-start gap-1 rounded-2xl px-4 py-3 text-left ring-1 transition active:scale-[0.99] ${
                  s.id === activeSessionId
                    ? 'bg-sage-100 ring-sage-300'
                    : 'bg-white/60 ring-sage-200/60 hover:bg-white/80'
                }`}
              >
                <span className="text-xs text-sage-500">
                  {new Date(s.createdAt).toLocaleString()}
                  {' · '}
                  {t('chat.turns_count', { count: s.turnCount })}
                </span>
                <span className="text-sm text-sage-800 leading-snug line-clamp-2">
                  {s.preview ?? <em className="text-sage-500">{t('chat.no_messages')}</em>}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      {sessionsQuery.isLoading && <LoadingDots />}
      {sessionsQuery.isError && !locked && (
        <ErrorState onRetry={() => sessionsQuery.refetch()} />
      )}

      {/* Active conversation */}
      {activeSessionId && activeQuery.data && (
        <section>
          <SectionHeader title={t('chat.conversation')} />
          <div className="flex flex-col gap-3 rounded-3xl bg-white/60 p-4 ring-1 ring-sage-200/60">
            {activeQuery.data.messages.length === 0 && (
              <p className="py-6 text-center text-sm text-sage-500">{t('chat.empty_session')}</p>
            )}
            {activeQuery.data.messages.map((m, i) => (
              <ChatBubble key={i} turn={m} />
            ))}
            {postMutation.isPending && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-sage-100 px-4 py-2 text-sm text-sage-700">
                  <LoadingDots compact />
                </div>
              </div>
            )}
            <div ref={transcriptEndRef} />
          </div>

          <div className="mt-3 flex flex-col gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend()
              }}
              maxLength={800}
              disabled={activeQuery.data.turnCount >= activeQuery.data.turnLimit}
              placeholder={
                activeQuery.data.turnCount >= activeQuery.data.turnLimit
                  ? t('chat.session_exhausted')
                  : t('chat.placeholder')
              }
              rows={3}
              className="w-full resize-none rounded-2xl border border-sage-200 bg-white/80 p-3 text-sm text-sage-800 placeholder-sage-400 focus:border-sage-400 focus:outline-none disabled:bg-sage-50 disabled:text-sage-500"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-sage-500">
                {t('chat.turns_left', {
                  used: activeQuery.data.turnCount,
                  limit: activeQuery.data.turnLimit,
                })}
              </span>
              <Button
                size="md"
                onClick={handleSend}
                loading={postMutation.isPending}
                disabled={
                  !draft.trim() ||
                  activeQuery.data.turnCount >= activeQuery.data.turnLimit
                }
                rightIcon={<Send className="h-4 w-4" strokeWidth={1.8} />}
              >
                {t('chat.send')}
              </Button>
            </div>
            {postMutation.isError && (
              <p className="text-sm text-copper">
                {postMutation.error instanceof ApiError
                  ? postMutation.error.body.error.message
                  : (postMutation.error as Error).message}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Empty state when there are sessions but none selected */}
      {!activeSessionId &&
        sessionsQuery.data &&
        sessionsQuery.data.length === 0 && (
          <div className="rounded-3xl bg-white/60 p-6 text-center ring-1 ring-sage-200/60">
            <p className="text-sage-700">{t('chat.start_first')}</p>
            <Button
              size="lg"
              className="mt-3"
              onClick={() => startMutation.mutate()}
              loading={startMutation.isPending}
            >
              {t('chat.new')}
            </Button>
          </div>
        )}
    </div>
  )
}

function ChatBubble({ turn }: { turn: ChatTurn }) {
  const isUser = turn.role === 'user'
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? 'bg-sage-200 text-sage-900'
            : 'bg-[#fbeadd] text-sage-800 ring-1 ring-[#e6c4a4]/60'
        }`}
      >
        {turn.content}
      </div>
    </div>
  )
}

